import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

type WordCourse = {
  id: string;
  name: string;
  displayOrder: number;
  level: number;
  courseDescription?: string;
  totalLesson?: number;
};

type WordPart = {
  id: string;
  partName: string;
  displayOrder: number;
  from: number;
  to: number;
  total: number;
  courseId: string;
  lessonsCount?: number;
};

type WordLesson = {
  id: string;
  displayOrder: number;
  from: string;
  to: string;
  fromOrder: string;
  toOrder: string;
  courseId: string;
  partId: string;
};

const partSchema = yup.object().shape({
  id: yup.string().required("パートIDが必須です。"),
  partName: yup.string().required("パート名が必須です。"),
  displayOrder: yup.number().required("表示順が必須です。").min(1),
  from: yup.number().required("開始番号が必須です。").min(1),
  to: yup.number().required("終了番号が必須です。").min(1),
  total: yup.number().required("総数が必須です。").min(1),
});

const lessonSchema = yup.object().shape({
  id: yup.string().required("レッスンIDが必須です。"),
  displayOrder: yup.number().required("表示順が必須です。").min(1),
  from: yup.string().required("開始語が必須です。"),
  to: yup.string().required("終了語が必須です。"),
  fromOrder: yup.string().required("開始順序が必須です。"),
  toOrder: yup.string().required("終了順序が必須です。"),
});

const WordCourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  
  const [course, setCourse] = useState<WordCourse | null>(null);
  const [parts, setParts] = useState<WordPart[]>([]);
  const [lessons, setLessons] = useState<WordLesson[]>([]);
  const [activePart, setActivePart] = useState<WordPart | null>(null);
  const [activeLesson, setActiveLesson] = useState<WordLesson | null>(null);
  const [isEditPartMode, setIsEditPartMode] = useState(false);
  const [isEditLessonMode, setIsEditLessonMode] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState<string>("");

  const {
    register: registerPart,
    handleSubmit: handleSubmitPart,
    formState: { errors: partErrors },
    reset: resetPart,
  } = useForm({
    resolver: yupResolver(partSchema),
  });

  const {
    register: registerLesson,
    handleSubmit: handleSubmitLesson,
    formState: { errors: lessonErrors },
    reset: resetLesson,
    setValue,
  } = useForm({
    resolver: yupResolver(lessonSchema),
  });

  const fetchCourse = async () => {
    if (!id) return;
    
    dispatch(setIsLoading(true));
    try {
      const response = await API.get(`word-course/${id}`);
      const { data } = response.data;
      setCourse(data);
    } catch (err) {
      console.error(err);
      toast.error("コースの取得に失敗しました。");
      navigate("/content/word-courses");
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchParts = async () => {
    if (!id) return;
    
    try {
      const response = await API.get(`word-parts/${id}`);
      const { data } = response.data;
      setParts(data || []);
    } catch (err) {
      console.error(err);
      toast.error("パートの取得に失敗しました。");
    }
  };

  const fetchLessons = async () => {
    if (!id || !selectedPartId) return;
    
    try {
      const response = await API.get(`word-lessons/${id}/${selectedPartId}`);
      const { data } = response.data;
      setLessons(data || []);
    } catch (err) {
      console.error(err);
      toast.error("レッスンの取得に失敗しました。");
    }
  };

  const handleCreatePart = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      
      const partData = {
        ...data,
        courseId: id,
      };
      
      await API.post(`word-part/${id}`, partData);
      toast.success("パートが作成されました。");
      fetchParts();
      resetPart();
      document.querySelector<HTMLButtonElement>("#modalPart .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "パートの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdatePart = async (data: any) => {
    if (!activePart) return;
    
    try {
      dispatch(setIsApiLoading(true));
      
      const partData = {
        ...data,
        courseId: id,
      };
      
      await API.put(`word-part/${id}/${activePart.id}`, partData);
      toast.success("パートが更新されました。");
      fetchParts();
      resetPart();
      document.querySelector<HTMLButtonElement>("#modalPart .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "パートの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeletePart = async (part: WordPart) => {
    if (!window.confirm(`「${part.partName}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`word-part/${id}/${part.id}`);
      toast.success("パートが削除されました。");
      fetchParts();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "パートの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleCreateLesson = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      
      const lessonData = {
        ...data,
        courseId: id,
        partId: selectedPartId,
      };
      
      await API.post(`word-lesson/${id}/${selectedPartId}`, lessonData);
      toast.success("レッスンが作成されました。");
      fetchLessons();
      resetLesson();
      document.querySelector<HTMLButtonElement>("#modalLesson .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "レッスンの作成に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleUpdateLesson = async (data: any) => {
    if (!activeLesson) return;
    
    try {
      dispatch(setIsApiLoading(true));
      
      const lessonData = {
        ...data,
        courseId: id,
        partId: selectedPartId,
      };
      
      await API.put(`word-lesson/${id}/${selectedPartId}/${activeLesson.id}`, lessonData);
      toast.success("レッスンが更新されました。");
      fetchLessons();
      resetLesson();
      document.querySelector<HTMLButtonElement>("#modalLesson .btn-close")?.click();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "レッスンの更新に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteLesson = async (lesson: WordLesson) => {
    if (!window.confirm(`「レッスン${lesson.displayOrder}」を削除しますか？`)) return;
    
    try {
      dispatch(setIsApiLoading(true));
      await API.delete(`word-lesson/${id}/${selectedPartId}/${lesson.id}`);
      toast.success("レッスンが削除されました。");
      fetchLessons();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || "レッスンの削除に失敗しました。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const openCreatePartModal = () => {
    setIsEditPartMode(false);
    setActivePart(null);
    resetPart({
      id: "",
      partName: "",
      displayOrder: parts.length + 1,
      from: 1,
      to: 1,
      total: 1,
    });
  };

  const openEditPartModal = (part: WordPart) => {
    setIsEditPartMode(true);
    setActivePart(part);
    resetPart({
      id: part.id,
      partName: part.partName,
      displayOrder: part.displayOrder,
      from: part.from,
      to: part.to,
      total: part.total,
    });
  };

  const openCreateLessonModal = () => {
    setIsEditLessonMode(false);
    setActiveLesson(null);
    resetLesson({
      id: "",
      displayOrder: lessons.length + 1,
      from: "",
      to: "",
      fromOrder: "",
      toOrder: "",
    });
  };

  const openEditLessonModal = (lesson: WordLesson) => {
    setIsEditLessonMode(true);
    setActiveLesson(lesson);
    resetLesson({
      id: lesson.id,
      displayOrder: lesson.displayOrder,
      from: lesson.from,
      to: lesson.to,
      fromOrder: lesson.fromOrder,
      toOrder: lesson.toOrder,
    });
  };

  const handlePartSelection = (partId: string) => {
    setSelectedPartId(partId);
    setLessons([]); // Clear lessons when part changes
  };

  useEffect(() => {
    if (id) {
      fetchCourse();
      fetchParts();
    }
  }, [id]);

  useEffect(() => {
    if (selectedPartId) {
      fetchLessons();
    }
  }, [selectedPartId]);

  // Setup event listeners for parts
  useEffect(() => {
    const handlePartClick = (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.closest(".edit-part-btn")) {
        const partId = target.closest(".edit-part-btn")?.getAttribute("data-id");
        const part = parts.find((p) => p.id === partId);
        if (part) {
          openEditPartModal(part);
        }
      }
      
      if (target.closest(".delete-part-btn")) {
        const partId = target.closest(".delete-part-btn")?.getAttribute("data-id");
        const part = parts.find((p) => p.id === partId);
        if (part) {
          handleDeletePart(part);
        }
      }

      if (target.closest(".manage-lessons-btn")) {
        const partId = target.closest(".manage-lessons-btn")?.getAttribute("data-id");
        if (partId) {
          handlePartSelection(partId);
        }
      }
    };

    document.addEventListener("click", handlePartClick);
    
    return () => {
      document.removeEventListener("click", handlePartClick);
    };
  }, [parts]);

  // Setup event listeners for lessons
  useEffect(() => {
    const handleLessonClick = (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (target.closest(".edit-lesson-btn")) {
        const lessonId = target.closest(".edit-lesson-btn")?.getAttribute("data-id");
        const lesson = lessons.find((l) => l.id === lessonId);
        if (lesson) {
          openEditLessonModal(lesson);
        }
      }
      
      if (target.closest(".delete-lesson-btn")) {
        const lessonId = target.closest(".delete-lesson-btn")?.getAttribute("data-id");
        const lesson = lessons.find((l) => l.id === lessonId);
        if (lesson) {
          handleDeleteLesson(lesson);
        }
      }
    };

    document.addEventListener("click", handleLessonClick);
    
    return () => {
      document.removeEventListener("click", handleLessonClick);
    };
  }, [lessons]);

  if (!course && !loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              コースが見つかりません。
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>単語コース詳細</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Page Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="mb-1">単語コース詳細</h4>
                <p className="text-muted mb-0">{course?.name}</p>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/content/word-courses")}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  コース一覧に戻る
                </button>
              </div>
            </div>

            <Loading />
            {!loading && course && (
              <>
                {/* Card 1: Course Details */}
                <div className="card mb-4" style={{ height: 'auto' }}>
                  <div className="card-header">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      単語コース詳細
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card border-info">
                          <div className="card-body">
                            <h6 className="card-title text-info">
                              <i className="fas fa-info-circle me-2"></i>
                              コース情報
                            </h6>
                            <table className="table table-sm">
                              <tbody>
                                <tr>
                                  <td><strong>コース名:</strong></td>
                                  <td>{course.name}</td>
                                </tr>
                                <tr>
                                  <td><strong>表示順:</strong></td>
                                  <td>{course.displayOrder}</td>
                                </tr>
                                <tr>
                                  <td><strong>レベル:</strong></td>
                                  <td>{course.level}</td>
                                </tr>
                                <tr>
                                  <td><strong>総レッスン数:</strong></td>
                                  <td>{course.totalLesson}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card border-secondary">
                          <div className="card-body">
                            <h6 className="card-title text-secondary">
                              <i className="fas fa-file-text me-2"></i>
                              説明
                            </h6>
                            <p className="card-text">
                              {course.courseDescription || "説明がありません。"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Parts Management */}
                <div className="card mb-4" style={{ height: 'auto' }}>
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-puzzle-piece me-2"></i>
                      パート管理
                    </h5>
                    <button
                      className="btn btn-primary btn-sm"
                      data-bs-toggle="modal"
                      data-bs-target="#modalPart"
                      onClick={openCreatePartModal}
                    >
                      <i className="fas fa-plus me-2"></i>
                      パート追加
                    </button>
                  </div>
                  <div className="card-body">
                    {parts.length > 0 ? (
                      <div className="table-responsive">
                        <DataTable
                          key={`parts-table-${parts.length}`}
                          data={parts}
                          className="display"
                          options={{
                            autoWidth: true,
                            columnDefs: [
                              { targets: "_all", className: "text-start" },
                            ],
                            stripeClasses: [],
                            order: [[1, "asc"]], // Sort by displayOrder
                            searching: true,
                            destroy: true,
                            info: true,
                            paging: true,
                            language: {
                              lengthMenu: "レコード数&nbsp;_MENU_",
                              emptyTable: "データがありません。",
                              search: "検索:",
                              paginate: {
                                first: "最初",
                                last: "最後",
                                next: "次へ",
                                previous: "前へ",
                              },
                            },
                          }}
                          columns={[
                            {
                              title: "ID",
                              data: "id",
                              className: "text-start",
                              orderable: true,
                              width: "100px",
                            },
                            {
                              title: "パート名",
                              data: "partName",
                              className: "text-start",
                              orderable: true,
                            },
                            {
                              title: "表示順",
                              data: "displayOrder",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "開始番号",
                              data: "from",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "終了番号",
                              data: "to",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "総数",
                              data: "total",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "レッスン数",
                              data: "lessonsCount",
                              className: "text-center",
                              orderable: true,
                              render: (data) => data || 0,
                            },
                            {
                              title: "操作",
                              data: null,
                              orderable: false,
                              className: "text-center",
                              render: (data, type, row) => {
                                return `
                                  <div class="d-flex justify-content-center gap-1">
                                    <button class="btn btn-sm btn-outline-primary edit-part-btn" data-bs-toggle="modal" data-bs-target="#modalPart" data-id="${row.id}" title="編集">
                                      <i class="fas fa-edit"></i>
                                    </button>
                                    <button class="btn btn-sm btn-outline-info manage-lessons-btn" data-id="${row.id}" title="レッスン一覧">
                                      <i class="fas fa-list"></i>
                                    </button>
                                    <button class="btn btn-sm btn-outline-danger delete-part-btn" data-id="${row.id}" title="削除">
                                      <i class="fas fa-trash"></i>
                                    </button>
                                  </div>
                                `;
                              },
                            },
                          ]}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">パートがありません。</p>
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#modalPart"
                          onClick={openCreatePartModal}
                        >
                          <i className="fas fa-plus me-2"></i>
                          最初のパートを作成
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card 3: Lessons Management */}
                <div className="card" style={{ height: 'auto' }}>
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-list me-2"></i>
                      レッスン管理
                      {selectedPartId && (
                        <span className="badge bg-info ms-2">
                          {parts.find(p => p.id === selectedPartId)?.partName}
                        </span>
                      )}
                    </h5>
                    <button
                      className="btn btn-primary btn-sm"
                      data-bs-toggle="modal"
                      data-bs-target="#modalLesson"
                      onClick={openCreateLessonModal}
                      disabled={!selectedPartId}
                    >
                      <i className="fas fa-plus me-2"></i>
                      レッスン追加
                    </button>
                  </div>
                  <div className="card-body">
                    {!selectedPartId ? (
                      <div className="text-center py-4">
                        <p className="text-muted">パートを選択してレッスンを管理してください。</p>
                        <p className="text-muted">上のパート管理テーブルの <i className="fas fa-list text-info"></i> ボタンをクリックしてください。</p>
                      </div>
                    ) : lessons.length > 0 ? (
                      <div className="table-responsive">
                        <DataTable
                          key={`lessons-table-${lessons.length}-${selectedPartId}`}
                          data={lessons}
                          className="display"
                          options={{
                            autoWidth: true,
                            columnDefs: [
                              { targets: "_all", className: "text-start" },
                            ],
                            stripeClasses: [],
                            order: [[1, "asc"]], // Sort by displayOrder
                            searching: true,
                            destroy: true,
                            info: true,
                            paging: true,
                            language: {
                              lengthMenu: "レコード数&nbsp;_MENU_",
                              emptyTable: "データがありません。",
                              search: "検索:",
                              paginate: {
                                first: "最初",
                                last: "最後",
                                next: "次へ",
                                previous: "前へ",
                              },
                            },
                          }}
                          columns={[
                            {
                              title: "ID",
                              data: "id",
                              className: "text-start",
                              orderable: true,
                              width: "100px",
                            },
                            {
                              title: "表示順",
                              data: "displayOrder",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "開始語",
                              data: "from",
                              className: "text-start",
                              orderable: true,
                            },
                            {
                              title: "終了語",
                              data: "to",
                              className: "text-start",
                              orderable: true,
                            },
                            {
                              title: "開始順序",
                              data: "fromOrder",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "終了順序",
                              data: "toOrder",
                              className: "text-center",
                              orderable: true,
                            },
                            {
                              title: "操作",
                              data: null,
                              orderable: false,
                              className: "text-center",
                              render: (data, type, row) => {
                                return `
                                  <div class="d-flex justify-content-center gap-2">
                                    <button class="btn btn-sm btn-outline-primary edit-lesson-btn" data-bs-toggle="modal" data-bs-target="#modalLesson" data-id="${row.id}" title="編集">
                                      <i class="fas fa-edit"></i>
                                    </button>
                                    <button class="btn btn-sm btn-outline-danger delete-lesson-btn" data-id="${row.id}" title="削除">
                                      <i class="fas fa-trash"></i>
                                    </button>
                                  </div>
                                `;
                              },
                            },
                          ]}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">このパートにはレッスンがありません。</p>
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#modalLesson"
                          onClick={openCreateLessonModal}
                        >
                          <i className="fas fa-plus me-2"></i>
                          最初のレッスンを作成
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Part Modal */}
        <Modal
          id="modalPart"
          title={isEditPartMode ? "パート編集" : "パート作成"}
        >
          <Form onSubmit={handleSubmitPart(isEditPartMode ? handleUpdatePart : handleCreatePart)}>
            <Form.Group className="mb-3">
              <Form.Label>パートID <span className="text-danger">*</span></Form.Label>
              {isEditPartMode ? (
                <div className="form-control-plaintext">
                  <strong>{activePart?.id}</strong>
                </div>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="パートIDを入力"
                  isInvalid={!!partErrors.id}
                  {...registerPart("id")}
                />
              )}
              {!isEditPartMode && (
                <Form.Control.Feedback type="invalid">
                  {partErrors.id?.message}
                </Form.Control.Feedback>
              )}
              {isEditPartMode && (
                <Form.Text className="text-muted">
                  編集時はIDを変更できません。
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>パート名 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="パート名を入力"
                isInvalid={!!partErrors.partName}
                {...registerPart("partName")}
              />
              <Form.Control.Feedback type="invalid">
                {partErrors.partName?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>表示順 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="1"
                isInvalid={!!partErrors.displayOrder}
                {...registerPart("displayOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {partErrors.displayOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>開始番号 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    isInvalid={!!partErrors.from}
                    {...registerPart("from")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {partErrors.from?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>終了番号 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    isInvalid={!!partErrors.to}
                    {...registerPart("to")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {partErrors.to?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>総数 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    isInvalid={!!partErrors.total}
                    {...registerPart("total")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {partErrors.total?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditPartMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>

        {/* Lesson Modal */}
        <Modal
          id="modalLesson"
          title={isEditLessonMode ? "レッスン編集" : "レッスン作成"}
        >
          <Form onSubmit={handleSubmitLesson(isEditLessonMode ? handleUpdateLesson : handleCreateLesson)}>
            <Form.Group className="mb-3">
              <Form.Label>レッスンID <span className="text-danger">*</span></Form.Label>
              {isEditLessonMode ? (
                <div className="form-control-plaintext">
                  <strong>{activeLesson?.id}</strong>
                </div>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="レッスンIDを入力"
                  isInvalid={!!lessonErrors.id}
                  {...registerLesson("id")}
                />
              )}
              {!isEditLessonMode && (
                <Form.Control.Feedback type="invalid">
                  {lessonErrors.id?.message}
                </Form.Control.Feedback>
              )}
              {isEditLessonMode && (
                <Form.Text className="text-muted">
                  編集時はIDを変更できません。
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>表示順 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                min="1"
                isInvalid={!!lessonErrors.displayOrder}
                {...registerLesson("displayOrder")}
              />
              <Form.Control.Feedback type="invalid">
                {lessonErrors.displayOrder?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>開始語 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="開始語を入力"
                    isInvalid={!!lessonErrors.from}
                    {...registerLesson("from")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {lessonErrors.from?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>終了語 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="終了語を入力"
                    isInvalid={!!lessonErrors.to}
                    {...registerLesson("to")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {lessonErrors.to?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>開始順序 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="例: 1"
                    isInvalid={!!lessonErrors.fromOrder}
                    {...registerLesson("fromOrder")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {lessonErrors.fromOrder?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>終了順序 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="例: 20"
                    isInvalid={!!lessonErrors.toOrder}
                    {...registerLesson("toOrder")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {lessonErrors.toOrder?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            {/* Information Note */}
            <div className="alert alert-info mb-3">
              <h6 className="alert-heading">
                <i className="fas fa-info-circle me-2"></i>
                開始順序・終了順序について
              </h6>
              <p className="mb-2">
                <strong>開始順序</strong>と<strong>終了順序</strong>は、このレッスンに含まれる単語の範囲を決定します。
              </p>
              <p className="mb-2">
                <strong>例:</strong> 開始順序「1」、終了順序「20」の場合、順序1番目から20番目までの単語（計20個）がこのレッスンに含まれます。
              </p>
              <p className="mb-0">
                <strong>注意:</strong> 順序番号は単語データベースの並び順に基づいています。正確な範囲を指定してください。
              </p>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary">
                {isEditLessonMode ? "更新" : "作成"}
              </button>
            </div>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default WordCourseDetail;